/**
 * PLT-5741: inline SVG icons for the chat feedback control.
 *
 * The Upright design system has no thumbs-up / thumbs-down glyphs and
 * `lucide-react` (which assistant-ui's own action bar uses) is not a direct
 * dependency of this app, so we inline the six glyphs the control needs. Each
 * is a plain `FC<SVGProps<SVGSVGElement>>` so it drops straight into the design
 * system `IconButton`'s `icon` prop. Stroke uses `currentColor` and the fill
 * variants paint `currentColor` too, so the resting/hover/active colours are
 * driven entirely by CSS on the button (see upright-chat-theme.css).
 *
 * The copy + check glyphs mirror the ones in `chat-table.tsx` so the chat
 * feedback row's copy button reads identically to the table copy button.
 *
 * `CheckIcon` marks the copy button's copied state. It stays generic — no
 * feedback-specific sizing or colour, sized and coloured from the caller's own
 * context — but a new caller outside this control should take
 * `CheckStreamlineCoreLine` from `@upright/ui/icons` instead: these glyphs are
 * inlined because the design system has no thumbs-up / thumbs-down, which is not
 * true of a checkmark, and a hand-rolled glyph beside a Streamline one differs
 * in both stroke weight and ink extent.
 */
import type { FC, SVGProps } from 'react';

const BASE_PROPS: SVGProps<SVGSVGElement> = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 15,
  height: 15,
  viewBox: '0 0 20 20',
  'aria-hidden': true,
};

export const CopyIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg
    {...BASE_PROPS}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x={6} y={6} width={10} height={11} rx={1.5} />
    <path d="M12 6V4.5A1.5 1.5 0 0 0 10.5 3H4.5A1.5 1.5 0 0 0 3 4.5v8A1.5 1.5 0 0 0 4.5 14H6" />
  </svg>
);

/** Close (×) glyph for the reason popover's dismiss button. */
export const CloseIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg
    {...BASE_PROPS}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M5 5l10 10M15 5 5 15" />
  </svg>
);

export const CheckIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg
    {...BASE_PROPS}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M4 10.5 8 14.5 16 5.5" />
  </svg>
);

/** Thumbs-up, outline (resting / un-voted). */
export const ThumbsUpIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg
    {...BASE_PROPS}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M6 9v8H4a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1h2Z" />
    <path d="M6 9l3.2-6.1a.9.9 0 0 1 1.2-.4c.8.4 1.3 1.2 1.3 2.1V7h3.6a1.6 1.6 0 0 1 1.6 1.9l-1.1 6A1.6 1.6 0 0 1 14.6 16H6" />
  </svg>
);

/** Thumbs-up, filled (positive vote active). */
export const ThumbsUpFilledIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg {...BASE_PROPS} fill="currentColor" stroke="currentColor" strokeWidth={0.75} strokeLinejoin="round" {...props}>
    <path d="M6 9v7.6H4a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1h2Z" />
    <path d="M6 8.8l3.2-6.1a.9.9 0 0 1 1.2-.4c.8.4 1.3 1.2 1.3 2.1V7h3.6a1.6 1.6 0 0 1 1.6 1.9l-1.1 6A1.6 1.6 0 0 1 14.6 16.4H6Z" />
  </svg>
);

/** Thumbs-down, outline (resting / un-voted). */
export const ThumbsDownIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg
    {...BASE_PROPS}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M14 11V3h2a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2Z" />
    <path d="M14 11l-3.2 6.1a.9.9 0 0 1-1.2.4c-.8-.4-1.3-1.2-1.3-2.1V13H4.6A1.6 1.6 0 0 1 3 11.1l1.1-6A1.6 1.6 0 0 1 5.4 4H14" />
  </svg>
);

/** Thumbs-down, filled (negative vote active). */
export const ThumbsDownFilledIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg {...BASE_PROPS} fill="currentColor" stroke="currentColor" strokeWidth={0.75} strokeLinejoin="round" {...props}>
    <path d="M14 11.2V3.6h2a1 1 0 0 1 1 1V10a1 1 0 0 1-1 1h-2Z" />
    <path d="M14 11.2l-3.2 6.1a.9.9 0 0 1-1.2.4c-.8-.4-1.3-1.2-1.3-2.1V13H4.6A1.6 1.6 0 0 1 3 11.1l1.1-6A1.6 1.6 0 0 1 5.4 3.6H14Z" />
  </svg>
);
